import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Server, Check, X, Zap, Cloud, DollarSign } from "lucide-react";

export default function Compare() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="compare-page" className="page-enter pt-24">
      <section className="py-20 text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
            style={{ background: "rgba(0,243,255,0.1)", border: "1px solid rgba(0,243,255,0.3)", color: "#00F3FF" }}>
            <Server size={12} /> THE CLOUD SHOWDOWN
          </div>
          <h1 className="font-orbitron font-900 text-4xl lg:text-5xl text-white mb-6">
            IndiaCloud vs The <span className="text-gradient">Giants</span>
          </h1>
          <p className="font-outfit text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
            Why pay exorbitant bandwidth fees to foreign clouds when you can get superior 
            local performance inside India for a fraction of the cost? See how we stack up against AWS and DigitalOcean.
          </p>
        </div>
      </section>

      <section className="py-10 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: "rgba(0,243,255,0.15)", background: "rgba(10,10,18,0.7)" }}>
            <table className="w-full min-w-max text-left font-outfit">
              <thead>
                <tr style={{ background: "rgba(20,20,30,0.9)", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <th className="px-6 py-5 font-rajdhani font-700 text-sm text-gray-400 uppercase tracking-widest">Comparison Point</th>
                  <th className="px-6 py-5 font-orbitron font-700 text-lg text-white text-center border-x" style={{ borderColor: "rgba(0,243,255,0.2)", background: "rgba(0,243,255,0.05)" }}>
                    IndiaCloud
                  </th>
                  <th className="px-6 py-5 font-orbitron font-700 text-lg text-gray-300 text-center border-r" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
                    AWS (EC2)
                  </th>
                  <th className="px-6 py-5 font-orbitron font-700 text-lg text-gray-300 text-center">
                    DigitalOcean
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Predictable Pricing", ic: "Yes", aws: "No (Complex Billing)", do: "Yes" },
                  { label: "Free DDoS Protection", ic: "Included (1 Tbps)", aws: "Paid Extra (Shield)", do: "Basic Included" },
                  { label: "Bandwidth Allowances", ic: "Massive (8TB+)", aws: "Extremely Expensive", do: "Standard (1-2TB)" },
                  { label: "Customer Support", ic: "Human Support 24x7", aws: "Paid ($29+/mo)", do: "Ticket Based" },
                  { label: "NVMe Storage", ic: "Standard on all SSD plans", aws: "EBS Premium Tier", do: "Standard" },
                  { label: "Dedicated IP", ic: "Included Free", aws: "Charge per IP", do: "Included Free" },
                  { label: "Datacenter Location", ic: "Delhi, Mumbai & Global", aws: "Mumbai/Hyd", do: "Bangalore" },
                  { label: "Monthly Cost (Equivalent 2 Core / 2GB)", ic: "₹599 / $7.15", aws: "~$18.00 + BW", do: "$18.00" },
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)", background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.2)" }}>
                    <td className="px-6 py-4 text-sm text-gray-300 font-500">{row.label}</td>
                    
                    {/* IndiaCloud Column */}
                    <td className="px-6 py-4 text-center border-x relative" style={{ borderColor: "rgba(0,243,255,0.2)", background: "rgba(0,243,255,0.02)" }}>
                      <div className="flex flex-col items-center gap-1">
                        {row.ic === "Yes" || row.ic.includes("Included") ? <Check size={18} color="#00F3FF" /> : null}
                        <span className="text-sm font-700" style={{ color: "#00F3FF" }}>{row.ic}</span>
                      </div>
                    </td>
                    
                    {/* AWS Column */}
                    <td className="px-6 py-4 text-center text-sm text-gray-400 border-r" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
                      {row.aws === "No" || row.aws.includes("Expensive") || row.aws.includes("Paid Extra") ? <X size={16} className="mx-auto mb-1 text-red-400" /> : null}
                      {row.aws}
                    </td>
                    
                    {/* DO Column */}
                    <td className="px-6 py-4 text-center text-sm text-gray-400">
                      {row.do}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-14 text-center">
            <h3 className="font-orbitron font-700 text-2xl text-white mb-6">Stop overpaying for cloud infrastructure.</h3>
            <div className="flex items-center justify-center gap-4">
              <Link to="/vps-hosting">
                <button className="btn-primary px-8 py-3 font-700 flex items-center gap-2">
                  <Cloud size={16} /> VIEW PRICING
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
